<template>
  <div
    class="poll-display"
    :class="{ 'poll-display--embedded': embedded }"
  >
    <!-- Hero -->
    <div class="pd-hero">
      <div class="pd-hero-icon">
        <f7-icon f7="chart_bar_fill" size="20" color="#fff" />
      </div>
      <div class="pd-hero-text">
        <div class="pd-hero-title">{{ poll.title || $t("poll.questions") }}</div>
        <div class="pd-hero-meta">
          <span v-if="isCreator" class="pd-badge pd-badge--creator">
            <f7-icon f7="person_badge_plus" size="11" />
            {{ $t("poll.creator") }}
          </span>
          <span v-if="hasVoted" class="pd-badge pd-badge--voted">
            <f7-icon f7="checkmark_seal_fill" size="11" />
            {{ $t("poll.voted") }}
          </span>
          <span class="pd-meta-count">
            <f7-icon ios="f7:person_2" md="material:group" size="12" />
            {{ $t("poll.participants", { count: participantCount }) }}
          </span>
        </div>
      </div>
      <div class="pd-count-chip">
        <span class="pd-count-num">{{ questions.length }}</span>
        <span class="pd-count-label">{{ $t("poll.questions") }}</span>
      </div>
    </div>

    <div
      v-if="poll.description"
      class="pd-description"
      v-html="poll.description"
    ></div>

    <!-- ANSWER FORM -->
    <div v-if="!hasVoted && questions.length" class="pd-questions">
      <div
        v-for="(question, qIndex) in questions"
        :key="qIndex"
        class="pd-question"
      >
        <div class="pd-question-head">
          <span class="pd-question-num">{{ qIndex + 1 }}</span>
          <span class="pd-question-text">
            {{ question.questionText }}
            <span v-if="question.required" class="pd-required">*</span>
          </span>
          <span class="pd-type-badge" :class="'poll-type-' + question.type">
            <f7-icon :f7="questionTypeMeta(question.type).icon" size="11" />
            <span>{{ $t(questionTypeMeta(question.type).label) }}</span>
          </span>
        </div>

        <div v-if="question.type === 'radio'" class="pd-options">
          <label
            v-for="(option, oIndex) in question.options"
            :key="oIndex"
            class="pd-option"
            :class="{ 'pd-option--selected': responses[qIndex] === option.id }"
          >
            <f7-radio
              :name="`question-${qIndex}`"
              :value="option.id"
              :checked="responses[qIndex] === option.id"
              @change="updateResponse(qIndex, option.id)"
            />
            <span class="pd-option-label">{{ option.optionText }}</span>
          </label>
        </div>

        <div v-else-if="question.type === 'checkbox'" class="pd-options">
          <label
            v-for="(option, oIndex) in question.options"
            :key="oIndex"
            class="pd-option"
            :class="{
              'pd-option--selected': responses[qIndex]?.includes(option.id),
            }"
          >
            <f7-checkbox
              :name="`question-${qIndex}-${oIndex}`"
              :value="option.id"
              :checked="responses[qIndex]?.includes(option.id)"
              @change="
                updateMultipleResponse(qIndex, option.id, $event.target.checked)
              "
            />
            <span class="pd-option-label">{{ option.optionText }}</span>
          </label>
        </div>

        <div v-else-if="question.type === 'select'" class="pd-select">
          <f7-list-input
            type="select"
            :value="responses[qIndex]"
            :placeholder="$t('poll.selectOption')"
            @change="updateResponse(qIndex, $event.target.value)"
          >
            <option value=""></option>
            <option
              v-for="(option, oIndex) in question.options"
              :key="oIndex"
              :value="option.id"
            >
              {{ option.optionText }}
            </option>
          </f7-list-input>
        </div>

        <div v-else-if="TEXT_TYPES.includes(question.type)" class="pd-text-input">
          <f7-input
            :type="question.type"
            :name="`question-${qIndex}`"
            :label="question.questionText"
            :resizable="question.type === 'textarea'"
            :placeholder="$t('poll.yourAnswer')"
            :min="question.min"
            :max="question.max"
            :step="question.step"
            v-model="textResponses[qIndex]"
          />
        </div>

        <div v-else-if="question.type === 'range'" class="pd-range">
          <div class="pd-range-output">
            <span class="pd-range-value">
              {{ textResponses[qIndex] ?? (question.min ?? 0) }}
            </span>
            <span class="pd-range-scale">
              {{ question.min ?? 0 }} – {{ question.max ?? 100 }}
            </span>
          </div>
          <f7-range
            v-model="textResponses[qIndex]"
            :min="question.min ?? 0"
            :max="question.max ?? 100"
            :step="question.step ?? 1"
            :style="{
              '--f7-range-bar-active-bg-color': 'var(--avocash-green)',
            }"
          />
        </div>

        <div v-else-if="question.type === 'rating'" class="pd-rating">
          <div class="pd-rating-stars">
            <f7-icon
              v-for="star in 5"
              :key="star"
              :ios="star <= ratings[qIndex] ? 'f7:star_fill' : 'f7:star'"
              :md="
                star <= ratings[qIndex] ? 'material:star' : 'material:star_border'
              "
              size="28px"
              :color="star <= ratings[qIndex] ? '#C9A84C' : '#C7C7CC'"
              @click="updateRating(qIndex, star)"
              class="pd-rating-star"
            />
          </div>
          <div class="pd-rating-label">
            {{ ratings[qIndex] || 0 }}/5
          </div>
        </div>
      </div>

      <div class="pd-actions">
        <f7-button
          @click="submitResponse"
          :disabled="!canSubmit"
          large
          fill
          round
          class="pd-submit"
        >
          {{ $t("poll.submit") }}
        </f7-button>
      </div>
    </div>

    <!-- RESULTS -->
    <div v-if="hasVoted || showResults" class="pd-results">
      <div v-if="resultsEmpty" class="pd-empty">
        <div class="pd-empty-icon">
          <f7-icon f7="chart_bar" size="28" color="#B9C4BC" />
        </div>
        <span>{{ $t("poll.noResultsYet") }}</span>
      </div>
      <template v-else>
        <div
          v-for="(question, qIndex) in questions"
          :key="qIndex"
          class="pd-question-results"
        >
          <div class="pd-question-head">
            <span class="pd-question-num">{{ qIndex + 1 }}</span>
            <span class="pd-question-text">{{ question.questionText }}</span>
            <span class="pd-type-badge" :class="'poll-type-' + question.type">
              <f7-icon :f7="questionTypeMeta(question.type).icon" size="11" />
              <span>{{ $t(questionTypeMeta(question.type).label) }}</span>
            </span>
          </div>

          <!-- Choice results -->
          <div v-if="CHOICE_TYPES.includes(question.type)" class="pd-choice-results">
            <div class="pd-result-meta">
              <f7-icon ios="f7:person_2" md="material:group" size="14" />
              <span>{{ $t("poll.responses") }}: {{ getTotalResponses(question) }}</span>
            </div>
            <div
              class="pd-chart-container"
              v-if="getChartData(question).series[0]?.data?.some((n) => n > 0)"
            >
              <apexchart
                type="bar"
                :options="getBarChartOptions(question)"
                :series="getChartData(question).series"
                height="280"
              />
            </div>
            <div class="pd-option-results">
              <div
                v-for="option in question.options"
                :key="option.id"
                class="pd-option-result"
                :class="{
                  'pd-option-result--leader':
                    leadingOptionId(question) === option.id,
                }"
              >
                <span class="pd-option-text">
                  <f7-icon
                    v-if="isVotedOption(qIndex, option.id)"
                    ios="f7:checkmark_circle_fill"
                    md="material:check_circle"
                    size="16px"
                    color="#1A6B42"
                  />
                  <f7-icon
                    v-else-if="leadingOptionId(question) === option.id"
                    ios="f7:rosette"
                    md="material:emoji_events"
                    size="16px"
                    color="#C9A84C"
                  />
                  {{ option.optionText }}
                </span>
                <div class="pd-option-result-bar">
                  <f7-progressbar
                    :progress="getOptionPercentage(question, option)"
                    :style="{
                      '--f7-progressbar-progress-color':
                        leadingOptionId(question) === option.id
                          ? 'var(--avocash-green)'
                          : '#B9C4BC',
                    }"
                  />
                </div>
                <span class="pd-percentage">
                  {{ getOptionPercentage(question, option) }}%
                </span>
                <span class="pd-count">
                  ({{ getOptionCount(question, option) }})
                </span>
              </div>
            </div>
          </div>

          <!-- Rating results -->
          <div v-else-if="question.type === 'rating'" class="pd-rating-result">
            <div class="pd-rating-summary">
              <div class="pd-average-rating">
                <span class="pd-average-stars">
                  <f7-icon
                    v-for="star in 5"
                    :key="star"
                    :ios="
                      star <= Math.round(getAverageRating(qIndex))
                        ? 'f7:star_fill'
                        : 'f7:star'
                    "
                    :md="
                      star <= Math.round(getAverageRating(qIndex))
                        ? 'material:star'
                        : 'material:star_border'
                    "
                    size="20px"
                    color="#C9A84C"
                  />
                </span>
                <span class="pd-average-value">
                  {{ getAverageRating(qIndex) }}/5
                </span>
              </div>
              <div
                class="pd-chart-container"
                v-if="getRatingChartData(qIndex).series[0]?.data?.some((n) => n > 0)"
              >
                <apexchart
                  type="bar"
                  :options="getRatingChartOptions()"
                  :series="getRatingChartData(qIndex).series"
                  height="200"
                />
              </div>
            </div>
          </div>

          <!-- Range results -->
          <div v-else-if="question.type === 'range'" class="pd-range-result">
            <div class="pd-range-summary">
              <div class="pd-range-statistics">
                <div class="pd-stat">
                  <span class="pd-stat-label">{{ $t("poll.average") }}</span>
                  <span class="pd-stat-value">{{ getAverageRangeValue(qIndex) }}</span>
                </div>
                <div class="pd-stat">
                  <span class="pd-stat-label">{{ $t("poll.min") }}</span>
                  <span class="pd-stat-value">{{ getMinRangeValue(qIndex) }}</span>
                </div>
                <div class="pd-stat">
                  <span class="pd-stat-label">{{ $t("poll.max") }}</span>
                  <span class="pd-stat-value">{{ getMaxRangeValue(qIndex) }}</span>
                </div>
              </div>
              <div
                class="pd-chart-container"
                v-if="getRangeChartData(qIndex).series[0]?.data?.some((n) => n > 0)"
              >
                <apexchart
                  type="bar"
                  :options="getRangeChartOptions(question)"
                  :series="getRangeChartData(qIndex).series"
                  height="250"
                />
              </div>
            </div>
          </div>

          <!-- Text responses -->
          <div v-else class="pd-text-responses">
            <div class="pd-response-count">
              {{ $t("poll.responses") }}: {{ getTextResponses(qIndex).length }}
            </div>
            <div class="pd-responses-list" v-if="getTextResponses(qIndex).length > 0">
              <div
                v-for="response in getTextResponses(qIndex).slice(0, 5)"
                :key="response.id"
                class="pd-text-response"
              >
                <f7-icon ios="f7:person" md="material:person" size="14" />
                <span>{{ response.textResponse }}</span>
              </div>
              <div v-if="getTextResponses(qIndex).length > 5" class="pd-more-responses">
                {{ $t("poll.moreResponses", { count: getTextResponses(qIndex).length - 5 }) }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- RESULTS TOGGLE -->
    <div
      v-if="!hasVoted && (isCreator || poll.isResponseVisible) && questions.length"
      class="pd-results-toggle"
    >
      <f7-button @click="showResults = !showResults" small outline round class="pd-toggle-btn">
        <template #text>
          <f7-icon
            :ios="showResults ? 'f7:eye_slash' : 'f7:eye'"
            :md="showResults ? 'material:visibility_off' : 'material:visibility'"
            size="16px"
          />
          {{ showResults ? $t("poll.hideResults") : $t("poll.showResults") }}
        </template>
      </f7-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { usePollStore } from "../js/pinia-stores/poll.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import i18n from "@/js/i18n.js";
import { f7 } from "framework7-vue";

const props = defineProps({
  poll: {
    type: Object,
    required: true,
  },
  context: {
    type: String,
    default: "standalone",
  },
  embedded: {
    type: Boolean,
    default: false,
  },
});

const TEXT_TYPES = ["text", "textarea", "email", "tel", "url", "number", "date", "datetime-local"];
const CHOICE_TYPES = ["radio", "checkbox", "select"];

const QUESTION_TYPE_META = {
  radio: { label: "poll.singleChoice", icon: "checkmark_seal_fill" },
  checkbox: { label: "poll.multipleChoice", icon: "checkmark_seal_fill" },
  select: { label: "poll.dropDown", icon: "checkmark_seal_fill" },
  text: { label: "poll.short_text", icon: "doc_text" },
  textarea: { label: "poll.long_text", icon: "doc_text" },
  email: { label: "common.email", icon: "doc_text" },
  tel: { label: "common.phoneNumber", icon: "doc_text" },
  number: { label: "common.number", icon: "doc_text" },
  url: { label: "common.url", icon: "doc_text" },
  date: { label: "common.date", icon: "calendar" },
  "datetime-local": { label: "common.date_time", icon: "calendar" },
  rating: { label: "poll.rating", icon: "star" },
  range: { label: "common.range", icon: "chart_bar_fill" },
};

const responses = ref([]);
const textResponses = ref([]);
const ratings = ref([]);
const showResults = ref(false);
const submitted = ref(false);

const keycloakStore = useKeycloakStore();
const pollStore = usePollStore();

const questions = computed(() => props.poll.questions || []);

function questionTypeMeta(type) {
  return (
    QUESTION_TYPE_META[type] || { label: "poll.short_text", icon: "doc_text" }
  );
}

const isCreator = computed(
  () => !!props.poll.creator && props.poll.creator.userId === keycloakStore.subject
);

const myResponses = computed(() => {
  const list = props.poll.responses || [];
  return list.filter(
    (r) => (r.respondent?.userId || r.userId) === keycloakStore.subject
  );
});

const hasVoted = computed(() => {
  if (submitted.value) return true;
  return myResponses.value.length > 0;
});

const results = computed(() => pollStore.getPollResults(props.poll.id));

const resultsEmpty = computed(() => {
  if (!results.value) return true;
  return questions.value.every((question) => {
    const questionResults = results.value[question.id];
    return !questionResults || (questionResults.totalResponses || 0) === 0;
  });
});

const participantCount = computed(() => {
  const responseList = props.poll.responses || [];
  if (responseList.length > 0) {
    const unique = new Set(
      responseList
        .map((r) => r.respondent?.userId || r.userId)
        .filter(Boolean)
    );
    return unique.size || responseList.length;
  }
  let max = 0;
  questions.value.forEach((question) => {
    const total = results.value?.[question.id]?.totalResponses || 0;
    if (total > max) max = total;
  });
  return max;
});

const canSubmit = computed(() => {
  return questions.value.every((question, index) => {
    if (question.required) {
      if (question.type === "radio" || question.type === "select") {
        return responses.value[index] !== undefined && responses.value[index] !== "";
      } else if (question.type === "checkbox") {
        return (responses.value[index] || []).length > 0;
      } else if ([...TEXT_TYPES, "range"].includes(question.type)) {
        return (
          textResponses.value[index] !== undefined &&
          String(textResponses.value[index]).trim() !== ""
        );
      } else if (question.type === "rating") {
        return ratings.value[index] > 0;
      }
    }
    return true;
  });
});

function updateResponse(questionIndex, optionId) {
  responses.value[questionIndex] = optionId;
}

function updateMultipleResponse(questionIndex, optionId, checked) {
  if (!responses.value[questionIndex]) {
    responses.value[questionIndex] = [];
  }
  const current = responses.value[questionIndex];
  const optionIndex = current.indexOf(optionId);
  if (checked && optionIndex === -1) {
    current.push(optionId);
  } else if (!checked && optionIndex > -1) {
    current.splice(optionIndex, 1);
  }
}

function updateRating(questionIndex, rating) {
  ratings.value[questionIndex] = rating;
}

async function submitResponse() {
  if (!canSubmit.value) return;

  try {
    const answers = [];
    questions.value.forEach((question, index) => {
      let answer = null;
      if (question.type === "radio" || question.type === "select") {
        if (responses.value[index] !== undefined && responses.value[index] !== "") {
          answer = { selectedOptions: [responses.value[index]] };
        }
      } else if (question.type === "checkbox") {
        if ((responses.value[index] || []).length > 0) {
          answer = { selectedOptions: responses.value[index] };
        }
      } else if ([...TEXT_TYPES, "range"].includes(question.type)) {
        const value = textResponses.value[index];
        if (value !== undefined && String(value).trim() !== "") {
          answer = { answerText: value };
        }
      } else if (question.type === "rating") {
        if (ratings.value[index]) {
          answer = { rating: ratings.value[index] };
        }
      }

      if (answer) {
        answers.push({
          pollId: props.poll.id,
          questionId: question.id,
          userId: keycloakStore.subject,
          questionIndex: index,
          ...answer,
        });
      }
    });

    await pollStore.submitPollResponse({
      pollId: props.poll.id,
      responseData: { responses: answers },
    });
    submitted.value = true;
    await pollStore.fetchPollResults(props.poll.id);
    f7.toast.show({
      text: i18n.global.t("poll.responseSubmitted"),
      position: "center",
    });
  } catch (error) {
    console.error("Error submitting response:", error);
    f7.toast.show({
      text: i18n.global.t("common.error"),
      position: "center",
    });
  }
}

function getTotalResponses(question) {
  if (!results.value || !results.value[question.id]) return 0;
  return results.value[question.id].totalResponses || 0;
}

function getOptionCount(question, option) {
  if (!results.value || !results.value[question.id]) return 0;
  return results.value[question.id].optionCounts?.[option.id] || 0;
}

function getOptionPercentage(question, option) {
  const total = getTotalResponses(question);
  const count = getOptionCount(question, option);
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

function leadingOptionId(question) {
  const questionResults = results.value?.[question.id];
  if (!questionResults) return null;
  let lead = null;
  let max = 0;
  (question.options || []).forEach((option) => {
    const count = questionResults.optionCounts?.[option.id] || 0;
    if (count > max) {
      max = count;
      lead = option.id;
    }
  });
  return lead;
}

function isVotedOption(questionIndex, optionId) {
  const question = questions.value[questionIndex];
  if (!question) return false;
  const myResponse = myResponses.value.find(
    (r) => r.questionIndex === questionIndex || r.questionId === question.id
  );
  return (
    Array.isArray(myResponse?.selectedOptions) &&
    myResponse.selectedOptions.includes(optionId)
  );
}

function getQuestionResults(questionIndex) {
  const question = questions.value[questionIndex];
  if (!question || !results.value || !results.value[question.id]) return null;
  return results.value[question.id];
}

function getAverageRating(questionIndex) {
  const questionResults = getQuestionResults(questionIndex);
  return questionResults?.averageRating || 0;
}

function getAverageRangeValue(questionIndex) {
  const questionResults = getQuestionResults(questionIndex);
  return questionResults ? (questionResults.averageRangeValue || 0).toFixed(2) : 0;
}

function getMinRangeValue(questionIndex) {
  const questionResults = getQuestionResults(questionIndex);
  if (questionResults?.minRangeValue !== undefined) return questionResults.minRangeValue;
  return questions.value[questionIndex]?.min ?? 0;
}

function getMaxRangeValue(questionIndex) {
  const questionResults = getQuestionResults(questionIndex);
  if (questionResults?.maxRangeValue !== undefined) return questionResults.maxRangeValue;
  return questions.value[questionIndex]?.max ?? 100;
}

function getTextResponses(questionIndex) {
  const questionResults = getQuestionResults(questionIndex);
  return questionResults?.textResponses || [];
}

function getChartData(question) {
  const questionResults = results.value?.[question.id];
  const categories = question.options.map((option) => option.optionText);
  const data = question.options.map(
    (option) => questionResults?.optionCounts?.[option.id] || 0
  );
  return {
    series: [{ name: i18n.global.t("poll.responses"), data }],
    categories,
  };
}

function chartColor() {
  if (typeof document === "undefined") return "#1A6B42";
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue("--avocash-green")
      .trim() || "#1A6B42"
  );
}

function getBarChartOptions(question) {
  const chartData = getChartData(question);
  return {
    chart: {
      type: "bar",
      height: 280,
      toolbar: { show: false },
      fontFamily: "inherit",
      background: "transparent",
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: "55%",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      offsetX: 4,
      style: { fontSize: "11px", colors: ["#6b6b6b"] },
    },
    colors: [chartColor()],
    stroke: { show: true, width: 1, colors: ["#fff"] },
    grid: { borderColor: "#e8e8e8" },
    xaxis: { categories: chartData.categories },
    yaxis: { title: { text: "" }, labels: { style: { fontSize: "12px" } } },
    legend: { show: false },
    tooltip: { theme: "light" },
    noData: { text: i18n.global.t("poll.noResultsYet") },
  };
}

function getRatingChartData(questionIndex) {
  const questionResults = getQuestionResults(questionIndex);
  const ratingCounts = [0, 0, 0, 0, 0];
  if (questionResults?.ratingCounts) {
    for (let i = 1; i <= 5; i++) {
      ratingCounts[i - 1] = questionResults.ratingCounts[i] || 0;
    }
  }
  return {
    series: [{ name: i18n.global.t("poll.responses"), data: ratingCounts }],
  };
}

function getRatingChartOptions() {
  return {
    chart: {
      type: "bar",
      height: 200,
      toolbar: { show: false },
      fontFamily: "inherit",
      background: "transparent",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 4,
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -18,
      style: { fontSize: "11px", colors: ["#6b6b6b"] },
    },
    colors: [chartColor()],
    grid: { borderColor: "#e8e8e8" },
    xaxis: {
      categories: ["1", "2", "3", "4", "5"],
      title: { text: i18n.global.t("poll.rating") },
    },
    yaxis: { title: { text: "" } },
    legend: { show: false },
    tooltip: { theme: "light" },
    noData: { text: i18n.global.t("poll.noResultsYet") },
  };
}

function getRangeChartData(questionIndex) {
  const questionResults = getQuestionResults(questionIndex);
  if (!questionResults || !Array.isArray(questionResults.rangeValues)) {
    return { series: [], categories: [] };
  }
  const question = questions.value[questionIndex];
  const min = Math.floor(question?.min ?? 0);
  const max = Math.ceil(question?.max ?? 100);
  const step = Math.max(question?.step ?? 1, 1);
  if (min > max) return { series: [], categories: [] };

  const buckets = {};
  for (let i = min; i <= max && Object.keys(buckets).length < 500; i += step) {
    buckets[i] = 0;
  }

  questionResults.rangeValues.forEach((value) => {
    const roundedValue = Math.round(value / step) * step;
    if (Object.prototype.hasOwnProperty.call(buckets, roundedValue)) {
      buckets[roundedValue]++;
    }
  });

  return {
    series: [
      {
        name: i18n.global.t("poll.responses"),
        data: Object.values(buckets),
      },
    ],
    categories: Object.keys(buckets).map((k) => parseFloat(k)),
  };
}

function getRangeChartOptions(question) {
  const chartData = getRangeChartData(questions.value.indexOf(question));
  return {
    chart: {
      type: "bar",
      height: 250,
      toolbar: { show: false },
      fontFamily: "inherit",
      background: "transparent",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "75%",
        borderRadius: 4,
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -18,
      style: { fontSize: "11px", colors: ["#6b6b6b"] },
    },
    colors: [chartColor()],
    stroke: { show: true, width: 1, colors: ["#fff"] },
    grid: { borderColor: "#e8e8e8" },
    xaxis: {
      categories: chartData.categories,
      title: {
        text: question.questionText,
        style: { fontSize: "12px", fontWeight: 600 },
      },
      labels: {
        formatter: (val) => val.toFixed(0),
      },
    },
    yaxis: { title: { text: "" } },
    tooltip: {
      theme: "light",
      y: {
        formatter: (val) => `${val} ${i18n.global.t("poll.responses")}`,
      },
    },
    noData: { text: i18n.global.t("poll.noResultsYet") },
  };
}

watch(
  () => props.poll?.id,
  () => {
    responses.value = [];
    textResponses.value = [];
    ratings.value = [];
    showResults.value = false;
    submitted.value = false;
    pollStore.fetchPollResults(props.poll.id);
  },
  { immediate: true }
);
</script>

<style scoped>
/* Container */
.poll-display:not(.poll-display--embedded) {
  background: var(--f7-card-bg-color, #fff);
  border: 1px solid rgba(28, 28, 30, 0.08);
  border-radius: 16px;
  padding: 14px;
}
.poll-display--embedded {
  padding: 2px 0;
}

/* Hero */
.pd-hero {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, var(--avocash-green-light), #ffffff);
  border: 1px solid rgba(26, 107, 66, 0.14);
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 12px;
}
.pd-hero-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--avocash-green);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pd-hero-text {
  flex: 1;
  min-width: 0;
}
.pd-hero-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--avocash-pit);
  line-height: 1.25;
  word-break: break-word;
}
.pd-hero-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.pd-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}
.pd-badge--creator {
  color: var(--avocash-blue);
  background: var(--avocash-blue-light);
}
.pd-badge--voted {
  color: var(--avocash-green);
  background: var(--avocash-green-light);
}
.pd-meta-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #5f6b66;
}
.pd-count-chip {
  flex-shrink: 0;
  background: var(--f7-card-bg-color, #fff);
  border: 1px solid rgba(26, 107, 66, 0.22);
  border-radius: 12px;
  padding: 8px 10px;
  text-align: center;
  min-width: 58px;
}
.pd-count-num {
  display: block;
  font-size: 18px;
  font-weight: 800;
  color: var(--avocash-green);
  line-height: 1;
}
.pd-count-label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #5f6b66;
  margin-top: 3px;
}

.pd-description {
  margin: 0 4px 12px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant);
}

/* Questions */
.pd-questions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pd-question,
.pd-question-results {
  background: var(--avocash-surface);
  border: 1px solid rgba(28, 28, 30, 0.06);
  border-radius: 14px;
  padding: 12px;
}
.pd-question-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}
.pd-question-num {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: var(--avocash-green);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pd-question-text {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--avocash-pit);
  line-height: 1.35;
  word-break: break-word;
}
.pd-required {
  color: var(--avocash-terracotta);
  font-weight: 700;
  margin-left: 2px;
}
.pd-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: var(--avocash-green);
  background: var(--avocash-green-light);
  white-space: nowrap;
  flex-shrink: 0;
}
.poll-type-date,
.poll-type-datetime-local {
  color: var(--avocash-blue);
  background: var(--avocash-blue-light);
}
.poll-type-rating {
  color: #8a6d1f;
  background: var(--avocash-gold-light);
}
.poll-type-range {
  color: var(--avocash-purple);
  background: var(--avocash-purple-light);
}

/* Options */
.pd-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pd-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid rgba(28, 28, 30, 0.08);
  background: var(--f7-card-bg-color, #fff);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.pd-option--selected {
  border-color: var(--avocash-green);
  background: var(--avocash-green-light);
}
.pd-option-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--avocash-pit);
  word-break: break-word;
}
.pd-select {
  margin-top: 2px;
}
.pd-text-input {
  margin-top: 2px;
}

/* Range */
.pd-range {
  margin-top: 2px;
}
.pd-range-output {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.pd-range-value {
  font-size: 22px;
  font-weight: 700;
  color: var(--avocash-green);
}
.pd-range-scale {
  font-size: 12px;
  color: var(--f7-on-surface-variant);
}

/* Rating */
.pd-rating {
  margin-top: 2px;
}
.pd-rating-stars {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pd-rating-star {
  cursor: pointer;
}
.pd-rating-label {
  margin-top: 8px;
  font-size: 13px;
  color: var(--f7-on-surface-variant);
}

/* Submit */
.pd-actions {
  margin-top: 4px;
}
.pd-submit {
  --f7-button-fill-bg-color: var(--avocash-green);
  --f7-button-fill-text-color: #fff;
  width: 100%;
}

/* Results */
.pd-results {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 30px 12px;
  font-size: 14px;
  color: var(--f7-on-surface-variant);
}
.pd-empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--avocash-surface);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pd-result-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 12px;
  color: var(--f7-on-surface-variant);
}
.pd-chart-container {
  margin-bottom: 12px;
  padding: 10px;
  border-radius: 10px;
  background: var(--f7-card-bg-color, #fff);
  border: 1px solid rgba(28, 28, 30, 0.06);
}

/* Choice results */
.pd-option-results {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pd-option-result {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 3fr auto auto;
  align-items: center;
  gap: 8px;
}
.pd-option-result--leader .pd-option-text {
  font-weight: 700;
  color: var(--avocash-green);
}
.pd-option-text {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--avocash-pit);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pd-option-result-bar {
  min-width: 0;
}
.pd-percentage {
  min-width: 38px;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: var(--avocash-pit);
}
.pd-count {
  font-size: 11px;
  color: var(--f7-on-surface-variant);
}

/* Rating results */
.pd-rating-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pd-average-rating {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  background: var(--f7-card-bg-color, #fff);
  border: 1px solid rgba(201, 168, 76, 0.35);
}
.pd-average-stars {
  display: flex;
  gap: 2px;
}
.pd-average-value {
  font-size: 18px;
  font-weight: 700;
  color: #8a6d1f;
}

/* Range results */
.pd-range-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pd-range-statistics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.pd-stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: 10px;
  border-left: 3px solid var(--avocash-green);
  background: var(--f7-card-bg-color, #fff);
}
.pd-stat-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--f7-on-surface-variant);
}
.pd-stat-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--avocash-green);
}

/* Text responses */
.pd-response-count {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 500;
  color: var(--f7-on-surface-variant);
}
.pd-responses-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pd-text-response {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border-left: 3px solid var(--avocash-green);
  background: var(--f7-card-bg-color, #fff);
  font-size: 13px;
  color: var(--avocash-pit);
}
.pd-more-responses {
  padding: 4px 12px;
  font-size: 12px;
  font-style: italic;
  color: var(--avocash-green);
}

/* Results toggle */
.pd-results-toggle {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}
.pd-toggle-btn {
  --f7-button-border-color: var(--avocash-green);
  --f7-button-text-color: var(--avocash-green);
}
</style>
